import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    Loader2,
    Mail,
    Phone,
    Calendar,
    UserPlus,
    Trash2,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

const MyParalegals = ({ userData }) => {
    const [paralegals, setParalegals] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [newParalegal, setNewParalegal] = useState({
        name: "",
        email: "",
        phone: "",
    });

    // جلب الـ Paralegals
    useEffect(() => {
        const fetchParalegals = async () => {
            const token = localStorage.getItem("auth_token");
            if (!token) {
                setError("Authentication required.");
                setLoading(false);
                return;
            }

            try {
                const response = await api.get("/paralegals", {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (response.data.success) {
                    setParalegals(response.data.data || []);
                } else {
                    setError("No paralegals found.");
                }
            } catch (err) {
                const msg = err.response?.data?.message || "Failed to load paralegals.";
                setError(msg);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: msg,
                    confirmButtonColor: "#1e40af",
                });
            } finally {
                setLoading(false);
            }
        };

        fetchParalegals();
    }, []);

    // إضافة Paralegal
    const handleAdd = async (e) => {
        e.preventDefault();
        setSaving(true);

        const token = localStorage.getItem("auth_token");
        if (!token) return;

        const payload = {
            name: newParalegal.name,
            email: newParalegal.email,
            phone: newParalegal.phone,
        };

        try {
            const response = await api.post("/paralegals", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (response.data.success) {
                const added = response.data.data;
                setParalegals((prev) => [...prev, added]);
                setShowAddModal(false);
                setNewParalegal({ name: "", email: "", phone: "" });

                Swal.fire({
                    icon: "success",
                    title: "Paralegal Added!",
                    html: `
                        <p class="text-sm">Invitation sent to:</p>
                        <p class="font-medium">${newParalegal.email}</p>
                        ${added.temporary_password ? `<p class="text-xs mt-2">Temp Password: <strong>${added.temporary_password}</strong></p>` : ""}
                    `,
                    timer: 5000,
                    showConfirmButton: false,
                });
            }
        } catch (err) {
            const msg = err.response?.data?.message || "Failed to add paralegal.";
            Swal.fire({ icon: "error", title: "Error", text: msg, confirmButtonColor: "#1e40af" });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            icon: "warning",
            title: "Delete Paralegal?",
            text: `Remove "${name}"? This cannot be undone.`,
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc2626",
        });

        if (!result.isConfirmed) return;

        const token = localStorage.getItem("auth_token");
        try {
            await api.delete(`/paralegals/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setParalegals((prev) => prev.filter((p) => p.id !== id));
            Swal.fire({
                icon: "success",
                title: "Deleted!",
                timer: 2000,
                showConfirmButton: true,
                confirmButtonText: "OK",
                confirmButtonColor: "#2563eb",
                allowOutsideClick: false,
            });

        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Failed",
                text: err.response?.data?.message || "Could not delete.",
            });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <p className="text-red-700 font-medium">{error}</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Paralegals Dashboard</h2>
                    <p className="text-gray-600 mt-1">Manage your paralegal team</p>
                </div>
            </div>

            {/* List */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Team Paralegals ({paralegals.length})
                    </h3>
                    <motion.button
                        onClick={() => setShowAddModal(true)}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 flex items-center gap-2"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <UserPlus className="w-4 h-4" />
                        Add Paralegal
                    </motion.button>
                </div>

                {paralegals.length === 0 ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H9a2 2 0 01-2-2v-1a6 6 0 0112 0v1a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <p className="text-gray-600 font-medium">No paralegals yet</p>
                        <p className="text-gray-500 text-sm mt-1">Click "Add Paralegal" to invite your team</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {paralegals.map((p, i) => (
                            <motion.div
                                key={p.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-sm flex items-center justify-between"
                            >
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-semibold text-sm">
                                            {p.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
                                        </span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900">{p.name}</h4>
                                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                                            <span className="flex items-center gap-1">
                                                <Mail className="w-3.5 h-3.5" />
                                                {p.email}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <Phone className="w-3.5 h-3.5" />
                                                {p.phone || "—"}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="text-right">
                                        <div className="text-xs text-gray-500 mb-1">Joined</div>
                                        <div className="flex items-center gap-1 text-sm font-medium text-gray-700">
                                            <Calendar className="w-3.5 h-3.5" />
                                            {new Date(p.joined_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(p.id, p.name)}
                                        className="text-red-600 hover:text-red-800 p-2"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-xl p-6 w-full max-w-md"
                    >
                        <h3 className="text-xl font-semibold text-gray-900 mb-5">Invite New Paralegal</h3>
                        <form onSubmit={handleAdd} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                <input
                                    type="text"
                                    value={newParalegal.name}
                                    onChange={(e) => setNewParalegal({ ...newParalegal, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Full Name"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input
                                    type="email"
                                    value={newParalegal.email}
                                    onChange={(e) => setNewParalegal({ ...newParalegal, email: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="email@example.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                                <input
                                    type="tel"
                                    value={newParalegal.phone}
                                    onChange={(e) => setNewParalegal({ ...newParalegal, phone: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="+96279..."
                                    required
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    disabled={saving}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-70"
                                >
                                    {saving ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <UserPlus className="w-4 h-4" />
                                            Send Invitation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default MyParalegals;